import React, { useState } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField,
    IconButton, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
    TablePagination, Checkbox, Autocomplete, Chip, Box, Typography
} from '@mui/material';
import { Delete, FilterList} from '@mui/icons-material';
import { Project } from "../interfaces/Project";
import {
    deleteProject
} from '../services/projectService';
import '../App.css';
import { formatDateToDDMMYYYY } from '../utils/dateUtils';
import { getEnergyClassColor } from '../interfaces/EnergyClassColor';

interface ProjectTableProps {
    projects: Project[];
    onEditProject: (project: Project) => void;
    refreshProjects: () => void;
}

const ProjectTable: React.FC<ProjectTableProps> = ({ projects, onEditProject, refreshProjects }) => {
    const [filter, setFilter] = useState('');
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
    const [page, setPage] = useState(0); // Page actuelle
    const [rowsPerPage, setRowsPerPage] = useState(10); // Nombre de lignes par page
    const [selectedProjects, setSelectedProjects] = useState<number[]>([]); // Projets sélectionnés
    const [statusFilter, setStatusFilter] = useState<number[]>([]);
    const [numeroAdemeFilter, setNumeroAdemeFilter] = useState<string[]>([]);

    const handleDelete = async (projectId: number) => {
        try {
            await deleteProject(projectId);
        } catch (error) {
            console.error('Erreur lors de la suppression du projet:', error);
        }
    };

    const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFilter(e.target.value);
    };

    const openDeleteModal = (project: Project) => {
        setProjectToDelete(project);
        setIsDeleteModalOpen(true);
    };

    const closeDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setProjectToDelete(null);
    };

    const confirmDelete = async () => {
        if (projectToDelete) {
            try {
                await deleteProject(projectToDelete.id);
                refreshProjects();
            } catch (error) {
                console.error('Erreur lors de la suppression du projet:', error);
            } finally {
                closeDeleteModal();
            }
        }
    };

    const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            setSelectedProjects(filteredProjects.map(project => project.id));
        } else {
            setSelectedProjects([]);
        }
    };

    const handleSelectProject = (projectId: number) => {
        setSelectedProjects(prevSelected =>
            prevSelected.includes(projectId)
                ? prevSelected.filter(id => id !== projectId)
                : [...prevSelected, projectId]
        );
    };

    const handleStatusChange = (event: any, newValue: number[]) => {
        setStatusFilter(newValue);
    };

    const handleNumeroAdemeChange = (event: any, newValue: string[]) => {
        setNumeroAdemeFilter(newValue);
    };

    const filteredProjects = projects
        .filter(project =>
            project.libelle.toLowerCase().includes(filter.toLowerCase()) &&
            (statusFilter.length === 0 || statusFilter.includes(project.status)) &&
            (numeroAdemeFilter.length === 0 || numeroAdemeFilter.includes(project.numeroAdeme))
        );

    // Pagination logic
    const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const paginatedProjects = filteredProjects.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const uniqueNumeroAdemeOptions = [...new Set(projects.map(p => p.numeroAdeme).filter(Boolean))];

    return (
        <Paper elevation={3} sx={{ p: 3, width: '100%', position: 'relative' }}>

            {/* Panneau de filtres */}
            <Box mb={3} p={2} sx={{ border: '2px solid #4caf50', borderRadius: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    <FilterList sx={{ mr: 1, color: '#4caf50' }} />
                    Filtres
                </Typography>

                <Box sx={{ columns: 3, mt: 2 }}>
                    <TextField
                        label="Libellé de projet"
                        variant="outlined"
                        fullWidth
                        style={{ marginTop: 2 }}
                        margin="normal"
                        value={filter}
                        onChange={handleFilterChange}
                    />

                    <Autocomplete
                        multiple
                        options={[...new Set(projects.map(p => p.status).filter(Boolean))]}
                        value={statusFilter}
                        onChange={handleStatusChange}
                        renderTags={(values, getTagProps) =>
                            values.map((value, index) => (
                                <Chip
                                    label={value}
                                    {...getTagProps({ index })}
                                    color="primary"
                                    sx={{ margin: 0.5 }}
                                />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField {...params} variant="outlined" label="Status" placeholder="Choisir le status" />
                        )}
                    />

                    <Autocomplete
                        multiple
                        options={uniqueNumeroAdemeOptions}
                        value={numeroAdemeFilter}
                        onChange={handleNumeroAdemeChange}
                        renderTags={(values, getTagProps) =>
                            values.map((value, index) => (
                                <Chip
                                    label={value}
                                    {...getTagProps({ index })}
                                    color="secondary"
                                    sx={{ margin: 0.5 }}
                                />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField {...params} variant="outlined" label="N° Ademe" placeholder="Choisir le N° Ademe" />
                        )}
                    />
                </Box>
            </Box>

            {filteredProjects.length > 0 ? (
                <Box>

                    <Box
                        display="flex"
                        justifyContent="flex-end"
                        gap={1}
                    >
                        {selectedProjects.length > 0 && (
                            <>
                                <Button
                                    variant="contained"
                                    color="error"
                                    sx={{
                                        position: 'relative',
                                        top: 8,
                                        mb: 3
                                    }}
                                    onClick={() => {
                                        selectedProjects.forEach((prj) => {
                                            handleDelete(prj);
                                            refreshProjects();
                                        });

                                    }}
                                >
                                    Supprimer {selectedProjects.length} {selectedProjects.length > 1 ? 'projets' : 'projet'}
                                </Button>

                                <Button
                                    variant="contained"
                                    color="warning"
                                    sx={{
                                        position: 'relative',
                                        top: 8,
                                        mb: 3
                                    }}
                                    onClick={() => {
                                        // Action à réaliser sur les projets sélectionnés
                                        console.log('Action sur les projets sélectionnés:', selectedProjects);
                                    }}
                                >
                                    Télécharger {selectedProjects.length} {selectedProjects.length > 1 ? 'projets' : 'projet'}
                                </Button>
                            </>

                        )}
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{
                                position: 'relative',
                                top: 8,
                                mb: 3,
                            }}
                            onClick={() => {
                                refreshProjects()
                            }}
                        >
                            Rechercher
                        </Button>
                    </Box>

                    <TableContainer component={Paper} sx={{ border: '2px solid #2196f3' }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#2196f3' }} >
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={selectedProjects.length === filteredProjects.length}
                                            indeterminate={selectedProjects.length > 0 && selectedProjects.length < filteredProjects.length}
                                            onChange={handleSelectAll}
                                        />
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', color: 'white' }}>ID</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', color: 'white' }}>Code</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', color: 'white' }}>Libellé</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', color: 'white' }}>Client</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', color: 'white' }}>N° Ademe</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', color: 'white' }}>Classe Énergétique Actuelle</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', color: 'white' }}>Classe Énergétique Objectif</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', color: 'white' }}>Date de création</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', color: 'white' }}>Status</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', color: 'white' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedProjects.map((project) => (
                                    <TableRow
                                        key={project.id}
                                        sx={{
                                            backgroundColor: selectedProjects.includes(project.id) ? '#e3f2fd' : 'transparent',
                                            '&:hover': {
                                                backgroundColor: selectedProjects.includes(project.id) ? '#e3f2fd' : '#f5f5f5', // Couleur de surbrillance
                                                cursor: 'pointer'
                                            }
                                        }}
                                    >
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={selectedProjects.includes(project.id)}
                                                onChange={() => handleSelectProject(project.id)}
                                            />
                                        </TableCell>
                                        <TableCell align="center">{project.id}</TableCell>
                                        <TableCell align="center">{project.code}</TableCell>
                                        <TableCell align="center">{project.libelle}</TableCell>
                                        <TableCell align="center">{}</TableCell>
                                        <TableCell align="center">{project.numeroAdeme}</TableCell>
                                        <TableCell>
                                            <div className={`
                                                px-4 py-1 rounded-full text-center font-bold w-10 
                                                ${getEnergyClassColor(project.classeEnergetiqueActuel)}
                                            `}>
                                                {project.classeEnergetiqueActuel}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className={`
                                                px-4 py-1 rounded-full text-center font-bold w-10
                                                ${getEnergyClassColor(project.classeEnergetiqueObjectif)}
                                            `}>
                                                {project.classeEnergetiqueObjectif}
                                            </div>
                                        </TableCell>
                                        <TableCell align="center">{formatDateToDDMMYYYY(project.dateCreation)}</TableCell>
                                        <TableCell align="center">{project.status}</TableCell>
                                        <TableCell align="center">
                                            <IconButton onClick={() => openDeleteModal(project)} color="error" title={'Supprimer ' + project.libelle}>
                                                <Delete />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50]}
                        component="div"
                        count={filteredProjects.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Box>
            ) : (
                <p>Aucun projet trouvé.</p>
            )}

            {/* Modal de confirmation de suppression */}
            <Dialog open={isDeleteModalOpen} onClose={closeDeleteModal}>
                <DialogTitle>Confirmer la suppression</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Êtes-vous sûr de vouloir supprimer le projet "{projectToDelete?.libelle}" ?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDeleteModal} color="primary">
                        Annuler
                    </Button>
                    <Button onClick={confirmDelete} color="error">
                        Supprimer
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default ProjectTable;